import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { MessageCircle, X, Send, Loader2, Phone } from "lucide-react";
import { BIZ } from "@/data/site";

/**
 * Floating "Ask us anything" chat widget powered by the backend /api/chat/ask
 * endpoint. Renders on every marketing page. Session-scoped via localStorage.
 */
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: `Hi! I'm Wynridge's assistant. Ask me about our services, pricing, or service areas — or call/text ${BIZ.phone} anytime.`,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sessionId = (() => {
    let sid = localStorage.getItem("wynridge_chat_session");
    if (!sid) {
      sid = `w-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem("wynridge_chat_session", sid);
    }
    return sid;
  })();

  const send = async (e) => {
    e && e.preventDefault();
    const q = input.trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { from: "user", text: q }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/ask`, { question: q, session_id: sessionId });
      setMessages((m) => [...m, { from: "bot", text: data.answer }]);
    } catch (err) {
      const msg =
        err.response?.status === 429
          ? "Slow down a moment — please try again in a minute."
          : "I can't reach my brain right now. Please call or text (615) 519-2629.";
      setMessages((m) => [...m, { from: "bot", text: msg }]);
    } finally {
      setSending(false);
    }
  };

  const quickAsks = [
    "How much for a house wash?",
    "Do you clean roofs?",
    "Where do you service?",
    "Are you licensed & insured?",
  ];

  return (
    <>
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-widget-toggle"
        className={`fixed bottom-24 right-4 md:bottom-6 md:right-6 z-[70] rounded-full shadow-2xl transition-all ${
          open ? "bg-stone-800 hover:bg-stone-900" : "bg-brand-600 hover:bg-electric-500"
        } text-white p-4 hover:-translate-y-0.5`}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-40 right-4 md:bottom-20 md:right-6 z-[70] w-[calc(100vw-2rem)] max-w-sm bg-white rounded-3xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden"
          style={{ maxHeight: "min(600px, calc(100vh - 6rem))" }}
        >
          {/* Header */}
          <div className="bg-brand-700 text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-electric-500 flex items-center justify-center">
              <MessageCircle className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>
                Ask Wynridge
              </div>
              <div className="text-[10px] uppercase tracking-widest text-electric-300 font-semibold">
                Answers, instantly
              </div>
            </div>
            <a
              href={`tel:${BIZ.phoneTel}`}
              data-testid="chat-widget-call"
              className="text-white/80 hover:text-white"
              aria-label="Call"
              onClick={() => {
                if (typeof window !== "undefined" && window.gtag) {
                  window.gtag("event", "click_to_call", { source: "chat_widget" });
                }
              }}
            >
              <Phone className="w-4 h-4" />
            </a>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50" data-testid="chat-widget-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.from === "user"
                    ? "ml-auto bg-brand-600 text-white rounded-br-md"
                    : "bg-white border border-stone-200 text-stone-800 rounded-bl-md"
                }`}
              >
                {m.text}
              </div>
            ))}
            {sending && (
              <div className="bg-white border border-stone-200 text-stone-500 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                thinking...
              </div>
            )}
          </div>

          {/* Quick asks */}
          {messages.length <= 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2 bg-stone-50 border-t border-stone-200">
              {quickAsks.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => { setInput(q); setTimeout(() => send({ preventDefault(){} }), 30); }}
                  data-testid={`chat-quick-${q.slice(0,10).replace(/\W+/g,"-")}`}
                  className="text-xs bg-white border border-stone-200 hover:border-electric-500 hover:text-brand-700 text-stone-600 rounded-full px-3 py-1.5 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={send} className="p-3 flex items-center gap-2 border-t border-stone-200 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, pricing..."
              data-testid="chat-widget-input"
              disabled={sending}
              className="flex-1 px-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white"
              maxLength={500}
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              data-testid="chat-widget-send"
              className="p-2.5 rounded-full bg-brand-600 hover:bg-electric-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
